import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { useLayoutEffect } from '../../../hooks/use-isomorphic-layout-effect';
import type { IListPageLayoutFilterDialogProps } from './list-page-layout.types';
import { cn } from '@utils';

const POPOVER_OFFSET_PX = 4;
const POPOVER_MIN_WIDTH_PX = 280;
const POPOVER_VIEWPORT_BOTTOM_BUFFER_PX = 8;
const POPOVER_MIN_MAX_HEIGHT_PX = 160;

interface IAnchoredPopoverProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	anchorRef: React.RefObject<HTMLElement>;
	children: React.ReactNode;
	footer?: React.ReactNode;
	className?: string;
}

interface IPopoverPosition {
	top: number;
	left: number;
	maxHeight: number;
}

const useAnchoredPosition = (
	anchorRef: React.RefObject<HTMLElement>,
	open: boolean
): IPopoverPosition | null => {
	const [position, setPosition] = React.useState<IPopoverPosition | null>(null);

	useLayoutEffect(() => {
		if (!open) {
			setPosition(null);
			return;
		}

		const updatePosition = () => {
			const anchor = anchorRef.current;
			if (!anchor) return;
			const rect = anchor.getBoundingClientRect();
			const top = rect.bottom + POPOVER_OFFSET_PX;
			const availableHeight = window.innerHeight - top - POPOVER_VIEWPORT_BOTTOM_BUFFER_PX;
			setPosition({
				top,
				left: rect.left,
				maxHeight: Math.max(POPOVER_MIN_MAX_HEIGHT_PX, availableHeight),
			});
		};

		updatePosition();
		window.addEventListener('resize', updatePosition);
		window.addEventListener('scroll', updatePosition, true);
		return () => {
			window.removeEventListener('resize', updatePosition);
			window.removeEventListener('scroll', updatePosition, true);
		};
	}, [anchorRef, open]);

	return position;
};

const useOutsideClickAndEscape = (
	contentRef: React.RefObject<HTMLElement>,
	anchorRef: React.RefObject<HTMLElement>,
	open: boolean,
	onClose: () => void
) => {
	React.useEffect(() => {
		if (!open) return;

		const handleMouseDown = (event: MouseEvent) => {
			const target = event.target as Node | null;
			if (!target) return;
			if (contentRef.current?.contains(target)) return;
			if (anchorRef.current?.contains(target)) return;
			onClose();
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose();
		};

		document.addEventListener('mousedown', handleMouseDown);
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('mousedown', handleMouseDown);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [open, onClose, contentRef, anchorRef]);
};

const AnchoredPopover: React.FC<IAnchoredPopoverProps> = ({
	open,
	onOpenChange,
	anchorRef,
	children,
	footer,
	className,
}) => {
	const contentRef = React.useRef<HTMLDivElement>(null);
	const position = useAnchoredPosition(anchorRef, open);

	const handleClose = React.useCallback(() => onOpenChange(false), [onOpenChange]);
	useOutsideClickAndEscape(contentRef, anchorRef, open, handleClose);

	if (!open || !position || typeof document === 'undefined') return null;

	return createPortal(
		<div
			ref={contentRef}
			role="dialog"
			data-testid="list-page-layout-filter-dialog-popover"
			className={cn(
				'fixed z-[999] flex flex-col rounded-lg border border-borderPrimary bg-white shadow-lg',
				className
			)}
			style={{
				top: `${position.top}px`,
				left: `${position.left}px`,
				minWidth: `${POPOVER_MIN_WIDTH_PX}px`,
				maxHeight: `${position.maxHeight}px`,
			}}
		>
			<div className="flex-1 min-h-0 overflow-y-auto p-4">{children}</div>
			{footer && <div className="border-t border-borderPrimary p-3">{footer}</div>}
		</div>,
		document.body
	);
};

interface IMobileFilterSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	children: React.ReactNode;
	footer?: React.ReactNode;
	className?: string;
}

const MobileFilterSheet: React.FC<IMobileFilterSheetProps> = ({
	open,
	onOpenChange,
	children,
	footer,
	className,
}) => (
	<DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
		<DialogPrimitive.Portal>
			<DialogPrimitive.Overlay
				className="fixed inset-0 z-[999] bg-bgOverlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
				data-testid="list-page-layout-filter-dialog-overlay"
			/>
			<DialogPrimitive.Content
				data-testid="list-page-layout-filter-dialog-sheet"
				aria-modal="true"
				aria-describedby={undefined}
				className={cn(
					'fixed bottom-0 left-0 right-0 z-[999] flex max-h-[93vh] flex-col rounded-t-2xl bg-white data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
					className
				)}
			>
				<DialogPrimitive.Title className="sr-only">Filters</DialogPrimitive.Title>
				<div className="flex justify-center pt-3 pb-1">
					<div className="h-1 w-10 rounded-full bg-borderPrimary" />
				</div>
				<div className="flex-1 overflow-y-auto p-4">{children}</div>
				{footer && <div className="border-t border-borderPrimary p-3">{footer}</div>}
			</DialogPrimitive.Content>
		</DialogPrimitive.Portal>
	</DialogPrimitive.Root>
);

export const ListPageLayoutFilterDialog: React.FC<IListPageLayoutFilterDialogProps> = ({
	open,
	onOpenChange,
	anchorRef,
	isMobile,
	children,
	footer,
	className,
}) => {
	if (isMobile) {
		return (
			<MobileFilterSheet open={open} onOpenChange={onOpenChange} footer={footer} className={className}>
				{children}
			</MobileFilterSheet>
		);
	}

	return (
		<AnchoredPopover
			open={open}
			onOpenChange={onOpenChange}
			anchorRef={anchorRef}
			footer={footer}
			className={className}
		>
			{children}
		</AnchoredPopover>
	);
};
ListPageLayoutFilterDialog.displayName = 'ListPageLayoutFilterDialog';
